import React from "react";
import { Helmet } from "react-helmet";
import { Carousel } from "react-responsive-carousel";
import { useParams, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import axiosInstance from "../../helpers/axiosConfig";
import { playlist, collections, file } from "../../static/api";
import { normalizeCollectionFiles } from "../../helpers/normalizeCollectionFiles";
import { useFullscreenMode } from "../../hooks/useFullScreen";
import { fullScreen, exitScreen } from "../../helpers/screen";
import { Loader } from "../../components";
import { getVersion } from "../../helpers/version";
import { getPageTitle } from "../../helpers/envTitle";
import { getCurrentTemplate } from "../player/helpers/templateUtils";
import { useSlideshow } from "../player/hooks/useSlideshow";
import { useKeyboardNavigation } from "../player/hooks/useKeyboardNavigation";
import { SlideRenderer } from "../player/components/slideRenderer";
import { UserInfo } from "../../store/selectors";

import "react-responsive-carousel/lib/styles/carousel.min.css";
import "../dashboard/carousel/style.css";
import "animate.css";

/**
 * PlaylistPlayer - plays multiple compilations in sequence.
 *
 * URL: /playlist-player/:id
 *
 * Fetches the playlist, then for each item fetches the collection + files,
 * plays them using the same carousel/slide infrastructure as the regular
 * player, and auto-advances to the next compilation after playDuration seconds.
 */
export const PlaylistPlayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userInfo } = useSelector(UserInfo);
  useFullscreenMode();
  const carouselRef = React.useRef();

  // ── Playlist-level state ──
  const [playlistData, setPlaylistData] = React.useState(null);
  const [playlistLoading, setPlaylistLoading] = React.useState(true);
  const [playlistError, setPlaylistError] = React.useState(null);

  // ── Per-collection state ──
  const [currentItemIndex, setCurrentItemIndex] = React.useState(0);
  const [state, setState] = React.useState([]); // normalized files for current collection
  const [catalogData, setCatalogData] = React.useState(null);
  const [collectionLoading, setCollectionLoading] = React.useState(false);

  // ── Playback state ──
  const [play, setPlay] = React.useState(false);
  const [isStop, setIsStop] = React.useState(false);
  const [showModal, setShowModal] = React.useState(true);
  const [, setTimeRemaining] = React.useState(0); // eslint-disable-line no-unused-vars

  // ── 1. Fetch playlist ──
  React.useEffect(() => {
    let cancelled = false;
    const fetchPlaylist = async () => {
      setPlaylistLoading(true);
      setPlaylistError(null);
      try {
        const res = await axiosInstance.get(`${playlist}/${id}`);
        if (!cancelled) {
          setPlaylistData(res.data);
          setCurrentItemIndex(0);
        }
      } catch (err) {
        if (!cancelled) {
          setPlaylistError(
            err.response?.data?.message || err.message || "Failed to load playlist"
          );
        }
      } finally {
        if (!cancelled) setPlaylistLoading(false);
      }
    };
    fetchPlaylist();
    return () => { cancelled = true; };
  }, [id]);

  // ── 2. Fetch collection data for the current playlist item ──
  const items = playlistData?.items || [];
  const currentItem = items[currentItemIndex] || null;

  React.useEffect(() => {
    if (!currentItem || !userInfo?.id) return;

    let cancelled = false;
    const fetchCollection = async () => {
      setCollectionLoading(true);
      try {
        const [catalogRes, filesRes] = await Promise.all([
          axiosInstance.get(`${collections}/${currentItem.collectionId}`),
          axiosInstance.get(`${file}/${userInfo.id}/${currentItem.collectionId}`),
        ]);

        if (!cancelled) {
          const normalized = normalizeCollectionFiles(
            catalogRes.data,
            filesRes.data ?? []
          );
          setCatalogData(catalogRes.data);
          setState(normalized);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load collection for playlist item:", err);
          setState([]);
          setCatalogData(null);
        }
      } finally {
        if (!cancelled) setCollectionLoading(false);
      }
    };

    fetchCollection();
    return () => { cancelled = true; };
  }, [currentItem?.collectionId, userInfo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 3. Slideshow (reuse the existing hook for auto-advancing slides) ──
  const { slideIndex, setSlideIndex } = useSlideshow(
    state,
    play,
    catalogData,
    catalogData, // folderItem equivalent
    false,       // isPublic = false
    carouselRef
  );

  // ── 4. Playlist-level auto-advance timer ──
  const playDurationRef = React.useRef(0);
  React.useEffect(() => {
    playDurationRef.current = currentItem?.playDuration || 30;
  }, [currentItem?.playDuration]);

  React.useEffect(() => {
    if (!play || items.length === 0 || collectionLoading) return;

    const durationSec = playDurationRef.current;
    setTimeRemaining(durationSec);

    // Countdown timer (updates every second)
    const countdownId = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    // Advance timer
    const advanceId = setTimeout(() => {
      const nextIndex = (currentItemIndex + 1) % items.length;
      setCurrentItemIndex(nextIndex);
    }, durationSec * 1000);

    return () => {
      clearInterval(countdownId);
      clearTimeout(advanceId);
    };
  }, [play, currentItemIndex, items.length, collectionLoading]);

  // ── Close handler ──
  const handleClose = React.useCallback(async () => {
    exitScreen();
    await navigate("/playlists");
  }, [navigate]);

  // ── Keyboard navigation (reuse existing hook) ──
  useKeyboardNavigation(
    carouselRef,
    showModal,
    handleClose,
    isStop,
    setIsStop,
    play,
    setPlay,
    setShowModal
  );

  // ── Play handler ──
  const handlePlay = () => {
    setPlay(true);
    setShowModal(false);
    fullScreen();
  };

  // ── Loading states ──
  if (playlistLoading) {
    return <Loader />;
  }

  if (playlistError) {
    return (
      <div
        className="carousel__wrapper"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 18,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <p>Failed to load playlist</p>
          <p style={{ fontSize: 14, opacity: 0.7 }}>{playlistError}</p>
          <button
            onClick={() => navigate("/playlists")}
            style={{
              marginTop: 16,
              padding: "8px 24px",
              background: "#1890ff",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            Back to Playlists
          </button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        className="carousel__wrapper"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontSize: 18,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <p>This playlist has no compilations.</p>
          <button
            onClick={() => navigate("/playlists")}
            style={{
              marginTop: 16,
              padding: "8px 24px",
              background: "#1890ff",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 14,
            }}
          >
            Back to Playlists
          </button>
        </div>
      </div>
    );
  }

  // collectionName removed — overlay was removed

  return (
    <div className="carousel__wrapper" onDoubleClick={handleClose}>
      <Helmet>
        <title>{getPageTitle(`Playlist - ${playlistData?.name || ""}`)}</title>
      </Helmet>

      {/* Version / template info */}
      <div className="carousel__version">
        <div>{getVersion()}</div>
        <div className="carousel__template">
          {getCurrentTemplate(state, slideIndex)}
        </div>
      </div>

      {/* Playlist overlay removed per user request */}

      {/* Start modal */}
      {showModal && !play && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1001,
          }}
        >
          <div
            style={{
              background: "#1f1f1f",
              borderRadius: 12,
              padding: "32px 48px",
              textAlign: "center",
              color: "#fff",
              maxWidth: 480,
            }}
          >
            <h2 style={{ margin: "0 0 8px", fontSize: 22 }}>
              {playlistData?.name || "Playlist"}
            </h2>
            <p style={{ margin: "0 0 24px", opacity: 0.7, fontSize: 14 }}>
              {items.length} compilation{items.length !== 1 ? "s" : ""}
            </p>
            <button
              onClick={handlePlay}
              style={{
                padding: "12px 48px",
                background: "#1890ff",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              Play
            </button>
            <div style={{ marginTop: 16 }}>
              <button
                onClick={() => navigate("/playlists")}
                style={{
                  padding: "8px 24px",
                  background: "transparent",
                  color: "#aaa",
                  border: "1px solid #555",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                Back to Playlists
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Carousel */}
      {collectionLoading ? (
        <Loader />
      ) : state.length > 0 ? (
        <Carousel
          autoPlay={false}
          infiniteLoop
          ref={carouselRef}
          useKeyboardArrows={true}
          transitionTime={0}
          swipeable={false}
          showIndicators={false}
          showThumbs={false}
          showStatus={false}
          showArrows={false}
          onChange={(index) => {
            if (!play) {
              setSlideIndex(index);
            }
          }}
          selectedItem={slideIndex}
          key={`playlist-carousel-${currentItemIndex}-${state.length}`}
        >
          {SlideRenderer({
            state,
            slideIndex,
            catalogData,
            folderItem: catalogData,
            isPublic: false,
          })}
        </Carousel>
      ) : null}
    </div>
  );
};
